import { Children, Fragment, cloneElement, isValidElement } from "react";

// Page headline with a landing animation: the title is set as stacked lines, and on load each
// line rises out of a blur while the type widens from condensed to wide and its letter-spacing
// tightens (Mona Sans' variable width axis; .ft-land in animations.css). Lines arrive one
// after another.
//
// Lines: with a <br />, the title splits at each <br />. Otherwise the accent <span> becomes
// its own line, so "Meet <span>FrothAI</span>" lands as "Meet" then "FrothAI", the accent
// keeping its own colour or gradient.

const isLineBreak = child => isValidElement(child) && child.type === "br";
const isAccent = child => isValidElement(child) && child.type === "span";

// Plain lines are spans too, so they opt out of page rules such as `.ft-display span`
// (accent colour) or gradient-text spans, and take the headline's own colour.
const PLAIN = { color: "inherit", background: "none", WebkitTextFillColor: "currentcolor" };

function plainLine(nodes, i) {
  return (
    <span key={i} className="ft-land" style={{ ...PLAIN, "--ft-land-i": i }}>
      {/* Re-key: pieces pulled out of nested fragments can share keys. */}
      {nodes.map((n, j) => (isValidElement(n) ? cloneElement(n, { key: j }) : n))}
    </span>
  );
}

function accentLine(el, i) {
  return cloneElement(el, {
    key: i,
    className: `${el.props.className || ""} ft-land`.trim(),
    style: { ...el.props.style, "--ft-land-i": i },
  });
}

const isBlank = nodes => nodes.every(n => typeof n === "string" && !n.trim());

// Titles are often passed as <>...</>; look through fragments to the real pieces.
const flatten = children =>
  Children.toArray(children).flatMap(child =>
    isValidElement(child) && child.type === Fragment ? flatten(child.props.children) : [child]
  );

function toLines(children) {
  const items = flatten(children);
  const groups = [];
  let current = [];
  const flush = () => {
    if (current.length && !isBlank(current)) groups.push(current);
    current = [];
  };

  if (items.some(isLineBreak)) {
    items.forEach(child => (isLineBreak(child) ? flush() : current.push(child)));
    flush();
    return groups.map((g, i) => plainLine(g, i));
  }

  const lines = [];
  items.forEach(child => {
    if (isAccent(child)) {
      flush();
      groups.push(child);
    } else {
      current.push(child);
    }
  });
  flush();
  groups.forEach((g, i) => lines.push(Array.isArray(g) ? plainLine(g, i) : accentLine(g, i)));
  return lines;
}

export default function LandingHeadline({ as: Tag = "h1", className = "", children }) {
  return <Tag className={`${className} ft-land-h`.trim()}>{toLines(children)}</Tag>;
}
